'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchMentionNotifications, markMentionRead, markAllMentionsRead, MentionNotification } from '@/lib/db/mentions';

export interface AppNotification {
  id: string;
  contributionId: string;
  title: string;
  status: 'published' | 'rejected';
  rejectionReason?: string;
  readAt: string | null;
  createdAt: string;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  markAllRead: () => void;
  markRead: (id: string) => void;
  clearAll: () => void;
  // Mention notifications
  mentionNotifications: MentionNotification[];
  mentionUnreadCount: number;
  markMentionNotifRead: (id: string) => void;
  markAllMentionNotifsRead: () => void;
  totalUnreadCount: number;
}

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  markAllRead: () => {},
  markRead: () => {},
  clearAll: () => {},
  mentionNotifications: [],
  mentionUnreadCount: 0,
  markMentionNotifRead: () => {},
  markAllMentionNotifsRead: () => {},
  totalUnreadCount: 0,
});

export const useNotifications = () => useContext(NotificationContext);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [mentionNotifications, setMentionNotifications] = useState<MentionNotification[]>([]);
  const supabase = createClient();

  // Load persisted contribution notifications from localStorage on mount / user change
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    try {
      const stored = localStorage.getItem(`griott_notifs_${user.id}`);
      if (stored) setNotifications(JSON.parse(stored));
    } catch {
      setNotifications([]);
    }
  }, [user?.id]);

  // Persist to localStorage whenever notifications change
  useEffect(() => {
    if (!user) return;
    try {
      localStorage.setItem(`griott_notifs_${user.id}`, JSON.stringify(notifications));
    } catch {}
  }, [notifications, user?.id]);

  // Load mention notifications from Supabase
  useEffect(() => {
    if (!user) {
      setMentionNotifications([]);
      return;
    }
    fetchMentionNotifications(user.id).then(setMentionNotifications);
  }, [user?.id]);

  // Real-time subscription for new mention notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`mention_notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mention_notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        async (payload) => {
          const row = payload.new as any;
          // Fetch actor info
          let actorName: string | null = null;
          let actorAvatar: string | null = null;
          if (row.actor_id) {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('full_name, avatar_url')
              .eq('id', row.actor_id)
              .single();
            actorName = profile?.full_name || null;
            actorAvatar = profile?.avatar_url || null;
          }
          const notif: MentionNotification = {
            id: row.id,
            recipientId: row.recipient_id,
            actorId: row.actor_id,
            actorName,
            actorAvatar,
            contentType: row.content_type,
            contentId: row.content_id,
            contentSnippet: row.content_snippet,
            readAt: row.read_at,
            createdAt: row.created_at,
          };
          setMentionNotifications((prev) => {
            if (prev.some((n) => n.id === notif.id)) return prev;
            return [notif, ...prev].slice(0, 50);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Subscribe to real-time changes on contributions for this user
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`contributions:author:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contributions',
          filter: `author_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (row.status !== 'published' && row.status !== 'rejected') return;

          const notif: AppNotification = {
            id: `${row.id}-${row.status}-${Date.now()}`,
            contributionId: row.id,
            title: row.title || 'Your contribution',
            status: row.status,
            rejectionReason: row.rejection_reason ?? undefined,
            readAt: null,
            createdAt: new Date().toISOString(),
          };

          setNotifications((prev) => {
            const alreadyExists = prev.some(
              (n) => n.contributionId === row.id && n.status === row.status && !n.readAt
            );
            if (alreadyExists) return prev;
            return [notif, ...prev].slice(0, 50);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const markRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }))
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const markMentionNotifRead = useCallback(async (id: string) => {
    setMentionNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
    );
    await markMentionRead(id);
  }, []);

  const markAllMentionNotifsRead = useCallback(async () => {
    if (!user) return;
    setMentionNotifications((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }))
    );
    await markAllMentionsRead(user.id);
  }, [user?.id]);

  const unreadCount = notifications.filter((n) => !n.readAt).length;
  const mentionUnreadCount = mentionNotifications.filter((n) => !n.readAt).length;
  const totalUnreadCount = unreadCount + mentionUnreadCount;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAllRead,
        markRead,
        clearAll,
        mentionNotifications,
        mentionUnreadCount,
        markMentionNotifRead,
        markAllMentionNotifsRead,
        totalUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
